import { FacetsActionsProcessor } from 'search/facets/processors/actions/facets-actions-processor';
import {
  FacetState,
  FacetURLFilters,
  makeFacetState,
} from 'search/facets/models/facet-state';
import {
  DateFacet,
  Facet,
  FacetsLocked,
} from 'search/facets/models/resource-facet';
import { defaultDateFacets } from 'search/models/search-object';
import { copyObject } from 'search/reducers/utils';
import {
  FacetSchemaWithData,
  makeFacetSchemaWithData,
} from 'search/facets/models/facet-schema-with-data';
import { ResourceFacetBlock } from 'search/models/filter-panel';

export class DateFacetsActionsProcessor extends FacetsActionsProcessor {
  public processFacetsLoaded(oldState: FacetState, appliedFilters: FacetURLFilters,
                             schemaWithData: FacetSchemaWithData, facetsLocked: FacetsLocked): FacetState {
    const datesApplied = appliedFilters.dateFrom || appliedFilters.dateTo;

    const newSelectedAndApplied = datesApplied
      ? this.extractFromApplied(appliedFilters, schemaWithData, facetsLocked)
      : copyObject(oldState?.selected || defaultDateFacets);

    // @ts-ignore
    return makeFacetState({
      schemaWithData,
      selected: copyObject(newSelectedAndApplied),
      applied: datesApplied ? copyObject(newSelectedAndApplied) : null,
      expanded: oldState ? oldState.expanded : false,
      isLoading: false,
    });
  }

  public processBubblesReset(oldState: FacetState<DateFacet>, resetAll: boolean): FacetState {
    const stateWithoutApplied = oldState.update('applied', (facet) => {
      return !resetAll && facet?.isResetLocked ? facet : null;
    });
    return stateWithoutApplied.set('selected', stateWithoutApplied.applied || copyObject(defaultDateFacets));
  }

  public processFacetLocked(oldState: FacetState<DateFacet>): FacetState {
    const updated = this.updateResetLocked('selected', oldState);
    return this.updateResetLocked('applied', updated);
  }

  public processFacetSelected(oldState: FacetState<DateFacet>, selectedFacet: DateFacet): FacetState {
    return oldState.set('selected', selectedFacet);
  }

  public processExpandFacets(oldState: FacetState<DateFacet>, expanded: true, checkSelected: boolean): FacetState {
    return oldState.set('expanded', !checkSelected ? expanded : oldState.selected.selected);
  }

  public processReplaceSelectedFacets(oldState: FacetState, selectedFacets: Facet[]): FacetState {
    throw new Error('Unsupported operation');
  }

  public processApplyFacets(oldState: FacetState<DateFacet>): FacetState {
    const selected = oldState.selected;
    const datesNotEmpty = selected.dateFrom || selected.dateTo;
    return oldState.set('applied', selected.selected && datesNotEmpty ? selected : null);
  }

  public processFacetBlockSelected(oldState: FacetState<Facet[]>): FacetState {
    return oldState.set('selected', copyObject(defaultDateFacets));
  }

  public processBubbleRemoved(oldState: FacetState): FacetState {
    const resetSelectedState = oldState.set('selected', copyObject(defaultDateFacets));
    return resetSelectedState.set('applied', null);
  }

  public processCancelEditingFacets(oldState: FacetState): FacetState {
    return oldState.set('selected', copyObject(oldState.applied || defaultDateFacets));
  }

  public processResetFacetState(oldState: FacetState): FacetState {
    // @ts-ignore
    return makeFacetState({
      schemaWithData: makeFacetSchemaWithData({
        schema: oldState.schemaWithData.schema,
        data: null,
      }),
      selected: {
        dateFrom: '',
        dateTo: '',
        facetKey: ResourceFacetBlock.DATE,
        isResetLocked: false,
        selected: false,
        label: '',
        id: '',
      },
      applied: null,
      expanded: false,
      isLoading: false,
    });
  }

  public getAppliedFiltersForLock(schemaWithData: FacetSchemaWithData, filters: FacetURLFilters): string[] {
    const value = `${filters.dateFrom ? filters.dateFrom : ''}` +
      `${filters.dateFrom && filters.dateTo ? '—' : ''}` +
      `${filters.dateTo ? filters.dateTo : ''}`;
    return value ? [value] : null;
  }

  private updateResetLocked(field: 'selected' | 'applied', oldState: FacetState<DateFacet>) {
    return oldState.update(field, (facet) => {
      if (facet) {
        facet.isResetLocked = !facet.isResetLocked;
      }
      return facet;
    });
  }

  private extractFromApplied(appliedFilters: FacetURLFilters, schemaWithData: FacetSchemaWithData, facetsLocked: FacetsLocked) {
    const dateFacet = {} as DateFacet;
    const facetKey = schemaWithData.schema.key;
    const facetLockedByKey = facetsLocked.hasOwnProperty(facetKey) ? facetsLocked[facetKey] : [];
    dateFacet.dateFrom = String(appliedFilters.dateFrom || '');
    dateFacet.dateTo = String(appliedFilters.dateTo || '');
    dateFacet.selected = true;
    dateFacet.facetKey = facetKey;
    dateFacet.label = `${dateFacet.dateFrom ? dateFacet.dateFrom : ''}` +
      `${dateFacet.dateFrom && dateFacet.dateTo ? '—' : ''}` +
      `${dateFacet.dateTo ? dateFacet.dateTo : ''}`;
    dateFacet.id = dateFacet.label;
    dateFacet.isResetLocked = facetLockedByKey.includes(dateFacet.id);
    return dateFacet;
  }
}
